import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getUserId, planetGet } from "../api/planetApi";
import { destroy, initMap, setMarkers } from "../planet/mapAdapter";

/**
 * 感恩留言旁：小地图 + 标识位置 / 玩漂流瓶提醒
 */
export default function GratitudePlanetMapPromo({ compact = false }) {
  const userId = getUserId();
  const mapRef = useRef(null);
  const adapterRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    planetGet(`/profile/me?user_id=${userId}`)
      .then((data) => {
        if (!cancelled) setProfile(data.profile || null);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!mapRef.current) return undefined;
    if (adapterRef.current) {
      destroy(adapterRef.current);
      adapterRef.current = null;
    }
    const hasLoc =
      profile?.location_public &&
      profile?.fuzzy_lat != null &&
      profile?.fuzzy_lng != null;
    const center = hasLoc
      ? [profile.fuzzy_lat, profile.fuzzy_lng]
      : [31.2, 117.3];
    const zoom = hasLoc ? 7 : 4;
    const adapter = initMap(mapRef.current, { center, zoom });
    adapterRef.current = adapter;
    if (hasLoc) {
      setMarkers(adapter, [
        {
          lat: profile.fuzzy_lat,
          lng: profile.fuzzy_lng,
          title: `我 · ${profile.city || "已公开位置"}`,
        },
      ]);
    }
    setReady(true);
    // Leaflet 在隐藏容器里需延迟刷新尺寸
    const t = setTimeout(() => {
      try {
        adapter.map.invalidateSize();
      } catch {
        /* ignore */
      }
    }, 120);
    return () => {
      clearTimeout(t);
      destroy(adapter);
      adapterRef.current = null;
    };
  }, [profile]);

  const hasPublic =
    Boolean(profile?.location_public) &&
    profile?.fuzzy_lat != null &&
    profile?.fuzzy_lng != null;

  return (
    <aside
      className={`rounded-xl border border-teal-100 bg-gradient-to-b from-teal-50/90 to-white overflow-hidden flex flex-col ${
        compact ? "min-h-[200px]" : "min-h-[260px]"
      }`}
    >
      <div
        ref={mapRef}
        className={`w-full bg-slate-100 ${compact ? "h-36" : "h-44"}`}
        aria-label="漂流星球位置示意地图"
      />
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <p className="text-sm font-medium text-teal-950">
          {hasPublic
            ? `你已在地图标识：${profile.city || "模糊位置"}`
            : "在地图上标识你所在的位置"}
        </p>
        <p className="text-xs text-slate-500 leading-relaxed">
          {hasPublic
            ? "去单身地图看看同城的人，或投一封漂流瓶，把感恩传得更远。"
            : "公开城市级模糊位置后，别人能在漂流星球地图遇见你（不暴露精确定位）。"}
        </p>
        {!ready ? null : null}
        <div className="mt-auto flex flex-col gap-2 pt-1">
          <Link
            to="/planet/settings/privacy"
            className="text-center text-sm px-3 py-2 rounded-lg bg-teal-800 text-white hover:bg-teal-700"
          >
            {hasPublic ? "管理我的地图位置" : "去地图标识位置"}
          </Link>
          <Link
            to="/planet/bottles/create"
            className="text-center text-sm px-3 py-2 rounded-lg border border-teal-300 text-teal-900 hover:bg-teal-50"
          >
            可玩漂流瓶 · 投一封信
          </Link>
          <Link
            to="/planet/singles"
            className="text-center text-xs text-teal-700 underline"
          >
            打开单身地图
          </Link>
        </div>
      </div>
    </aside>
  );
}
